import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { useNotificationsDropdownActions } from '@/components/layout/Navigation/notifications/hooks/useNotificationsDropdownActions';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyP2 } from '@heidi/design-system/typography';
import { NotificationStatus } from '@heidi/schema/ml-scribe';
import { Delete, ListChecks, MoreHorizontal, Settings2, Trash2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

export const NotificationsDropdownMenu = () => {
  const {
    isInmailNotificationEnabled,
    isPending,
    notifications,
    mutateReadAllNotifications,
    mutateDeleteAllNotifications,
    mutateDeleteAllReadNotifications,
  } = useNotificationsDropdownActions();

  const isMobile = useIsMobile();
  const { open: openSettingsModal } = useSettingsModal();
  const intl = useIntl();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={isMobile ? 'touch' : 'xxs'}
          iconOnly
          // Skip `flush` on mobile — it would strip the padding that
          // gives the `touch` button its 44px hit area.
          flush={isMobile ? undefined : 'all'}
          // Gate the whole trigger while queries are pending: the
          // destructive items use `notifications?.length === 0` guards
          // that evaluate `undefined === 0` → false, so they would
          // otherwise fire mutations against unloaded state.
          disabled={isPending}
          aria-label={intl.formatMessage({
            defaultMessage: 'Notifications options',
            id: 'tsF5GAjCmB',
          })}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48" side="bottom" align="end" alignOffset={-40}>
        {isInmailNotificationEnabled && (
          <DropdownMenuItem
            className="flex cursor-pointer justify-start gap-2"
            onClick={() => {
              mutateReadAllNotifications();
            }}
            disabled={
              notifications?.length === 0 ||
              notifications?.every(
                (notification) => notification.status === NotificationStatus.READ
              )
            }
          >
            <ListChecks size={14} />
            <TypographyP2>
              <FormattedMessage defaultMessage="Mark all as read" id="/af+jQ8xvW" />
            </TypographyP2>
          </DropdownMenuItem>
        )}
        {isInmailNotificationEnabled && (
          <DropdownMenuItem
            variant="destructive"
            className="flex cursor-pointer justify-start gap-2"
            onClick={() => {
              mutateDeleteAllReadNotifications();
            }}
            disabled={
              notifications?.length === 0 ||
              notifications?.every(
                (notification) => notification.status === NotificationStatus.UNREAD
              )
            }
          >
            <Delete size={14} />
            <TypographyP2>
              <FormattedMessage defaultMessage="Delete all read" id="CpNQrgahgD" />
            </TypographyP2>
          </DropdownMenuItem>
        )}
        {isInmailNotificationEnabled && (
          <DropdownMenuItem
            variant="destructive"
            className="flex w-full cursor-pointer justify-start gap-2"
            onClick={() => {
              mutateDeleteAllNotifications();
            }}
            disabled={notifications?.length === 0}
          >
            <Trash2 size={14} />
            <TypographyP2>
              <FormattedMessage defaultMessage="Delete all" id="1L8nF4zVoa" />
            </TypographyP2>
          </DropdownMenuItem>
        )}

        {isInmailNotificationEnabled && <DropdownMenuSeparator />}

        <DropdownMenuItem
          onClick={() => {
            openSettingsModal('/settings/notifications', 'notifications_dropdown');
          }}
          className="flex cursor-pointer justify-start gap-2"
        >
          <Settings2 size={14} />
          <TypographyP2>
            <FormattedMessage defaultMessage="Settings" id="D3idYvSLF9" />
          </TypographyP2>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
